/* Port finder — "I have this camera and this lens, which port do I need?"
 *
 * The question the storefront could not answer: a shopper facing seven flat-port lengths has no way to
 * tell which suits their lens short of reading every listing. The catalog has always known — for most
 * (housing, lens) pairs exactly ONE flat port fits. So this resolves the flat port and merely OFFERS
 * the domes, because a lens that takes a dome usually takes six of them (4"/6"/8" × acrylic/optical
 * glass, plus surf editions) and that is a preference, not a compatibility question.
 *
 * Three steps, and unlike the gear finder the lens is a SELECTION rather than a filter, because the
 * answer depends on it. Shares dvl-gear-combo.js and the .dvl-g__* styles with the gear finder so the
 * two pages read as one family.
 *
 * The add-ons block leads with what a port is useless or unsafe without — grease, the single port O-ring
 * for the housing's bayonet, the housing O-ring service set, the zoom/focus gear — then covers sized to
 * the ports above. Each sits at the level it depends on in the document (grease at the root, the O-ring on
 * the housing, the gear on the lens row) and is referenced as [partKey, variantId], because only the
 * variant varies with the housing while the name and image belong to the cut.
 *
 * Data: assets/dvl-port.json, built by service/scripts/gear-finder/build_port_doc.py from the same
 * graph EQA's own matrix walks. Prices and stock come from /products/<handle>.js so they stay live and
 * in the shopper's market currency. A dome port product already CONTAINS its extension ring, so the
 * cart gets one line, and the gear a port includes free rides on it as a line item property the way
 * SPO records it — never a second line, which would charge for it or show a 0.00 to interpret.
 */
(() => {
  const money = (cents, fmt) =>
    cents == null ? "" : (fmt || "").replace(/\{\{\s*amount[^}]*\}\}/, (cents / 100).toFixed(2)) || (cents / 100).toFixed(2);

  // `shop.money_format` is a theme SETTING, and on some themes it is markup rather than text -
  // Fashionopolism ships "<span class=money>${{amount}} USD</span>" so currency-converter apps can find
  // the price. Through textContent those tags render literally, so a price is the one thing this file
  // writes as HTML: the format is the shop's, the amount is our own digits.
  const setMoney = (node, cents, fmt) => { node.innerHTML = money(cents, fmt); };

  /* One line into Shopify's own analytics bus: the custom pixel subscribes to `all_events` and
     forwards the dvl_* ones to EQA's beacon (see docs/analytics.md). This file therefore has no
     endpoint, no consent handling and no idea where the data goes. With no pixel installed
     `Shopify.analytics` is absent and this is a no-op — and it is wrapped because a storefront must
     never break for a measurement. */
  const track = (name, data) => {
    try {
      // Two transports on purpose. `Shopify.analytics.publish` is for the custom pixel, which is the
      // intended route and the only one that reaches checkout; `EqaBeacon` is the theme-side beacon
      // that carries these today, because a custom pixel cannot be installed by API. With both
      // present the pixel forwards nothing of ours twice — it subscribes to the bus, and EqaBeacon
      // posts directly — but page views WOULD double, which is why installing the pixel means
      // removing the beacon from layout/theme.liquid.
      const a = window.Shopify && window.Shopify.analytics;
      if (a && typeof a.publish === "function") a.publish(name, data || {});
      if (window.EqaBeacon) window.EqaBeacon.send(name, data || {});
    } catch (e) {
      /* never a reason for a page to fail */
    }
  };

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };


  /** A compact quick-add: a cart glyph plus a "+", the way a collection card's quick-add reads.
   *  The visible words are gone, so the button carries an aria-label AND a title — that label is all a
   *  screen reader or a hover has left, so it names the port and any gear that comes with it free. */
  const CART_PATH = "M1.5 2.5h2.2l2.2 9.3a1.8 1.8 0 0 0 1.75 1.4h7.1a1.8 1.8 0 0 0 1.75-1.33L18.5 5.5H5.2";
  const svgNS = "http://www.w3.org/2000/svg";
  const addButton = (label) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "dvl-g__btn dvl-g__add";
    b.setAttribute("aria-label", label);
    b.title = label;
    const svg = document.createElementNS(svgNS, "svg");
    svg.setAttribute("class", "dvl-g__add-icon");
    svg.setAttribute("viewBox", "0 0 20 20");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    const path = document.createElementNS(svgNS, "path");
    path.setAttribute("d", CART_PATH);
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "1.6");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    svg.append(path);
    for (const cx of [8.5, 15.2]) {
      const c = document.createElementNS(svgNS, "circle");
      c.setAttribute("cx", String(cx));
      c.setAttribute("cy", "16.6");
      c.setAttribute("r", "1.3");
      c.setAttribute("fill", "currentColor");
      svg.append(c);
    }
    b.append(svg);
    const plus = document.createElement("span");
    plus.className = "dvl-g__add-plus";
    plus.setAttribute("aria-hidden", "true");
    plus.textContent = "+";
    b.append(plus);
    return b;
  };

  /** Once it is in the cart there is no text left to change, so the glyph becomes a tick. */
  const markAdded = (btn, label) => {
    btn.classList.add("is-added");
    btn.setAttribute("aria-label", label);
    btn.title = label;
  };

  /** Housings whose zoom/focus gear is PAID, not complimentary.
   *
   *  Mirrors `PAID_ZOOM_GEAR_HOUSINGS` in service/src/lib/spo/previews.ts, which is the source of truth:
   *  A6XXX gears stopped being included and bill as an SPO add-on. Here that flips two things at once —
   *  the gear keeps its purchasable card in the add-ons, and the port does NOT carry a free-of-charge
   *  note — because doing both, or neither, either gives the part away or leaves no way to buy it. */
  const PAID_GEAR_HOUSINGS = new Set(["A6XXX"]);

  const BRAND_ORDER = ["Sony", "Canon", "Fujifilm", "Olympus", "Panasonic", "Nikon"];
  const byOrder = (list) => {
    const rank = (b) => {
      const i = BRAND_ORDER.indexOf(b);
      return i === -1 ? BRAND_ORDER.length : i;
    };
    return [...list].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  };

  class PortFinder {
    constructor(root) {
      this.root = root;
      this.fmt = root.dataset.moneyFormat || "";
      this.rootUrl = (root.dataset.root || "/").replace(/\/+$/, "");
      this.cache = new Map(); // handle -> live product JSON promise
      this.brand = null;
      this.housing = null;
      this.lens = null;
      this.wantHousing = (root.dataset.defaultHousing || "").trim(); // the housing to land on, if any
      this.init();
    }

    async init() {
      try {
        const doc = await fetch(this.root.dataset.src, { headers: { Accept: "application/json" } }).then((r) => r.json());
        this.doc = doc;
        this.parts = doc.parts || {};
        this.imgs = doc.imgs || [];
        this.rings = doc.rings || {};
        this.flatSets = doc.flatSets || [];
        this.domeSets = doc.domeSets || [];
        this.addonSets = doc.addonSets || [];
        this.byId = new Map((doc.housings || []).map((h) => [h.id, h]));
      } catch {
        this.root.append(el("p", "dvl-g__empty", "We could not load the port list just now. Please reload the page."));
        return;
      }
      this.brands = byOrder(new Set([...this.byId.values()].map((h) => h.b)));
      this.defaultHousing = this.resolveHousing(this.wantHousing);
      this.build();
      track("dvl_finder_opened", { surface: "port-finder" });
      this.applyUrl();
      window.addEventListener("popstate", () => this.applyUrl());
    }

    build() {
      this.root.textContent = "";
      const step = (n, label, field) => {
        const s = el("div", "dvl-g__step");
        const f = el("div", "dvl-g__field");
        f.append(field);
        s.append(el("span", "dvl-g__num", n), el("span", "dvl-g__label", label), f);
        return s;
      };

      this.chips = el("div", "dvl-g__chips");
      this.s1 = step("1", "Your camera brand", this.chips);

      this.hCombo = new window.DvlGearCombo({
        placeholder: "Search or choose your housing…",
        emptyText: "No housing matches",
        onChange: (v) => {
          this.housing = v || null;
          track("dvl_housing_chosen", { surface: "port-finder", housing: this.resolveHousing(v) || "" });
          this.lens = null; // a housing change invalidates the lens: the list is per housing
          this.paint();
          this.writeUrl(true);
        },
      });
      this.s2 = step("2", "Your underwater housing", this.hCombo.el);

      this.lCombo = new window.DvlGearCombo({
        placeholder: "Search or choose your lens…",
        emptyText: "No lens matches",
        onChange: (v) => {
          this.lens = v || null;
          this.paint();
          this.writeUrl(true);
        },
      });
      this.s3 = step("3", "Your lens", this.lCombo.el);
      // under the selector, not in the answer: it belongs to the lens the shopper just chose
      this.gearNote = el("p", "dvl-p__gearnote");
      this.gearNote.hidden = true;
      this.s3.querySelector(".dvl-g__field").append(this.gearNote);

      const steps = el("div", "dvl-g__steps");
      steps.append(this.s1, this.s2, this.s3);
      this.answer = el("div", "dvl-p__answer");
      this.toast = el("p", "dvl-g__toast");
      this.toast.hidden = true;
      this.toast.setAttribute("role", "status");
      this.root.append(steps, this.answer, this.toast);
      if (this.doc.generated) {
        this.root.append(el("p", "dvl-g__stamp", `Compatibility data generated ${this.doc.generated.slice(0, 10)}.`));
      }
    }

    /** The section's `default_housing` setting, matched against the document.
     *
     *  Tried most specific first, because these names overlap: the listing id `A7RV` belongs to a Sea
     *  Frogs housing while `A7RV/A7V` is the Salted Line one, so a "contains" test run first would
     *  answer the wrong housing for a setting of `A7RV`. Anything unmatched is ignored rather than
     *  guessed at, and the finder simply opens with the housing field empty as before. */
    resolveHousing(want) {
      if (!want) return null;
      const w = want.toLowerCase();
      const hs = [...this.byId.values()];
      const hit =
        hs.find((h) => h.id === want) ||
        hs.find((h) => h.n === want) ||
        hs.find((h) => h.id.toLowerCase() === w) ||
        hs.find((h) => h.n.toLowerCase() === w) ||
        hs.find((h) => h.n.toLowerCase().endsWith(` ${w}`)) ||
        null;
      return hit ? hit.id : null;
    }

    /* ---- state in the query string ------------------------------------------------------------ */

    applyUrl() {
      const p = new URLSearchParams(location.search);
      // A url naming NEITHER a housing nor a brand is the bare page, so the configured default applies.
      // Keying on the url rather than on "is this the first read" keeps the two in step: stepping Back
      // to the bare url shows what the bare url shows.
      const bare = !p.has("housing") && !p.has("brand");
      const h =
        this.byId.get(p.get("housing") || "") ||
        (bare && this.defaultHousing ? this.byId.get(this.defaultHousing) : null);
      const brand = p.get("brand");
      this.brand = h ? h.b : this.brands.includes(brand) ? brand : this.brands[0] || null;
      this.housing = h ? h.id : null;
      const lens = p.get("lens");
      this.lens = h && lens && h.lenses.some((l) => l.l === lens) ? lens : null;
      this.paint();
    }

    writeUrl(push) {
      const p = new URLSearchParams(location.search);
      const set = (k, v) => (v ? p.set(k, v) : p.delete(k));
      set("brand", this.brand);
      set("housing", this.housing);
      set("lens", this.lens);
      const qs = p.toString();
      const url = `${location.pathname}${qs ? `?${qs}` : ""}${location.hash}`;
      if (url === location.pathname + location.search + location.hash) return;
      try {
        history[push ? "pushState" : "replaceState"]({ dvlPort: 1 }, "", url);
      } catch {
        /* a sandboxed frame can refuse history writes */
      }
    }

    /* ---- render ------------------------------------------------------------------------------- */

    paint() {
      this.chips.textContent = "";
      for (const b of this.brands) {
        const c = el("button", "dvl-g__chip", b);
        c.type = "button";
        c.setAttribute("aria-pressed", String(b === this.brand));
        c.addEventListener("click", () => {
          this.brand = this.brand === b ? null : b;
          if (this.brand) track("dvl_brand_chosen", { surface: "port-finder", brand: this.brand });
          this.housing = null;
          this.lens = null;
          this.paint();
          this.writeUrl(true);
        });
        this.chips.append(c);
      }
      this.s1.dataset.done = this.brand ? "1" : "0";

      const hs = [...this.byId.values()].filter((h) => !this.brand || h.b === this.brand);
      hs.sort((a, b) => a.n.localeCompare(b.n));
      this.hCombo.setItems(hs.map((h) => ({ value: h.id, label: h.n })), { keepValue: true });
      this.hCombo.input.placeholder = this.brand ? "Search or choose your housing…" : "Pick a brand first";
      this.hCombo.disabled = !this.brand;
      this.hCombo.setValue(this.housing);
      this.s2.dataset.done = this.housing ? "1" : "0";

      const h = this.byId.get(this.housing || "");
      // a Sony housing puts the Sony lenses first — most shoppers own the camera maker's own glass —
      // then everything else alphabetically, which groups the third parties by brand anyway because a
      // lens name starts with its brand ("Sigma 16-28mm…", "Tamron 11-20mm…")
      const lenses = h
        ? [...h.lenses].sort(
            (a, b) =>
              Number(a.b !== h.b) - Number(b.b !== h.b) || a.l.localeCompare(b.l),
          )
        : [];
      this.lCombo.setItems(lenses.map((l) => ({ value: l.l, label: l.l })), { keepValue: true });
      this.lCombo.input.placeholder = h ? `Search ${lenses.length} lenses…` : "Pick a housing first";
      this.lCombo.disabled = !h;
      this.lCombo.setValue(this.lens);
      this.s3.dataset.done = this.lens ? "1" : "0";

      this.paintAnswer();
    }

    paintAnswer() {
      const a = this.answer;
      a.textContent = "";
      const h = this.byId.get(this.housing || "");
      if (!h) {
        this.paintGearNote(null, null);
        a.append(el("p", "dvl-g__empty", "Choose your camera and housing, then your lens."));
        return;
      }
      if (!this.lens) {
        this.paintGearNote(h, null);
        a.append(el("p", "dvl-g__empty", `Choose your lens to see the port it needs — ${h.lenses.length} fit the ${h.n}.`));
        return;
      }
      const row = h.lenses.find((l) => l.l === this.lens);
      this.paintGearNote(h, row);
      const flatKeys = (this.flatSets[row.f] || []).filter((s) => this.parts[s]);
      const flat = flatKeys.map((s) => this.parts[s]);
      // the catalog's note on this lens behind a port ("Optimal working range 24mm to 35mm…"), keyed by
      // flat SKU or dome RING code; the build has already dropped the gear boilerplate and scoped it
      const texts = this.doc.notes || [];
      const nt = {};
      for (const [k, i] of Object.entries(row.nt || {})) if (texts[i]) nt[k] = texts[i];
      const lensNote = (text, label) => {
        const p = el("p", "dvl-g__note dvl-p__lensnote");
        p.append(el("strong", null, label ? `${label}: ` : "Note: "));
        p.append(document.createTextNode(text));
        return p;
      };
      const dome = (this.domeSets[row.d] || []).map((s) => this.parts[s]).filter(Boolean);
      // [partKey, variantId] -> something renderable: the cut's display data with THIS housing's variant
      const ref = (r) => {
        if (!Array.isArray(r) || !r[0]) return null;
        const base = this.parts[r[0]];
        return base ? { ...base, vid: r[1] } : null;
      };
      // grease, then the single port ring (the seal this purchase needs), then the housing service set,
      // then the lens's gear
      // The gear LEADS: it is the only add-on a shopper cannot reason out for themselves — which gear
      // drives THIS lens on THIS housing is exactly what the catalog knows and a listing does not say.
      // Then grease, the port's own seal, the housing service set.
      const gearIsPaid = PAID_GEAR_HOUSINGS.has(h.id);
      const gear = ref(row.g);
      const lead = [gear, ref(this.doc.grease), ref(h.po), ref(h.or)].filter(Boolean);
      const addons = lead.concat(
        (this.addonSets[row.a] || []).map((s) => this.parts[s]).filter(Boolean),
      );

      // ---- flat: the resolved answer
      const fb = el("section", "dvl-p__block");
      fb.append(el("h2", "dvl-p__h", "Flat port"));
      if (!flat.length) {
        fb.append(el("p", "dvl-g__note", "This lens is used behind a dome port — there is no flat port for it."));
      } else {
        if (flat.length > 1) {
          fb.append(el("p", "dvl-g__note",
            `${flat.length} lengths fit this lens. They differ in working distance, not compatibility — the shorter the port, the closer you can focus.`));
        }
        const ul = el("ul", "dvl-g__list");
        for (const p of flat) ul.append(this.card(p, flat.length === 1));
        fb.append(ul);
        // one note when every length says the same (or there is one length), else each by name
        const said = flatKeys.filter((k) => nt[k]);
        if (said.length && (flat.length === 1 || (said.length === flat.length && said.every((k) => nt[k] === nt[said[0]])))) {
          fb.append(lensNote(nt[said[0]]));
        } else {
          for (const k of said) fb.append(lensNote(nt[k], this.parts[k].n));
        }
      }
      a.append(fb);

      // ---- dome: the offer, grouped by the ring each needs
      if (dome.length) {
        const db = el("section", "dvl-p__block");
        db.append(el("h2", "dvl-p__h", dome.length === 1 ? "Dome port" : `Dome ports (${dome.length})`));
        const byRing = new Map();
        for (const p of dome) {
          if (!byRing.has(p.er)) byRing.set(p.er, []);
          byRing.get(p.er).push(p);
        }
        for (const [er, list] of byRing) {
          const ring = this.rings[er];
          if (ring) {
            const note = el("p", "dvl-g__note");
            note.append(document.createTextNode(`These use extension ring ${er} — `));
            const label = `${ring.n}${ring.mm ? ` (${ring.mm}mm)` : ""}`;
            if (ring.h) {
              const a = el("a", null, label);
              a.href = this.href(ring, "ring"); // the ring was named but not reachable before
              note.append(a);
            } else {
              note.append(el("strong", null, label));
            }
            note.append(document.createTextNode(", which is included with the dome — you do not buy it separately."));
            db.append(note);
          }
          const ul = el("ul", "dvl-g__list");
          for (const p of list) ul.append(this.card(p, false, ring));
          db.append(ul);
          if (nt[er]) db.append(lensNote(nt[er], byRing.size > 1 ? `Note (${er})` : ""));
        }
        a.append(db);
      }

      // ---- what the catalog says belongs alongside those ports: size-matched neoprene covers and
      // spare spheres, from catn_flatport_addons / catn_domeport_addons. Not a cross-sell — the port
      // rows state them, and a shopper who buys a dome without a cover finds out the hard way.
      if (addons.length) {
        const ab = el("section", "dvl-p__block");
        ab.append(el("h2", "dvl-p__h", "Essential add-ons"));
        ab.append(el("p", "dvl-g__note",
          "The port O-ring is the seal between the port and your housing, and the service set replaces your " +
            "housing's own seals as well. Grease keeps them watertight" +
            (gearIsPaid ? "; the gear drives your lens from outside" : "") +
            ". Then covers and spheres sized to the ports above."));
        const ul = el("ul", "dvl-g__list");
        for (const p of addons) {
          // the gear comes WITH the port unless this housing bills for it
          const included = !gearIsPaid && gear && p === gear;
          ul.append(this.card(p, false, null, { included }));
        }
        ab.append(ul);
        a.append(ab);
      }
      this.hydrate(a);
    }


    /** An interned image reference -> its url. `i` is absent when a part has no image, and index 0 is a
     *  real reference, so this tests for a number rather than truthiness. */
    imgOf(o) {
      const i = o && o.i;
      return typeof i === "number" ? this.imgs[i] || "" : "";
    }

    /* ---- deep link: the product page must not ask again ---------------------------------------
       A port product page asks for the SAME three things this page just did — the SPO set is Camera
       Brand BUTTONS → per-brand Housing IMAGE_DROPDOWN → per-housing Lens IMAGE_DROPDOWN (Go
       spo/ports.go) — plus the Shopify variant for the length. So the link carries all of it:

         variant   Shopify's own parameter. Port SKUs are unique, so the variant id is unambiguous and
                   the theme opens on the right length with no product-page change at all.
         spo       SPO's own url-sync parameter: base64url of {elementName: value}, which its
                   spo-url-sync.js decodes and applies. Values are validated against the published
                   sets, so a key SPO does not recognise is ignored — a stale name degrades to "no
                   preselection", never to a broken page.
         housing   the listing id, as plain context for anything else on the page.

       The element names below mirror the Go generator and are the one place this couples to it:
         camera-brand                                   (spo/extension_rings.go brandButtonsElement)
         housings-for-<brand>-cameras                   (spo/ports.go portHousingDropdown)
         fp-<listingCode>-<portModel>-lens-for-<housingModel>-housing   (flat port set)
         dp-<erCode>-lens-for-<housingModel>-housing                    (dome port set)
         specify-your-lens-for-<brand>-<housingModel>-housing            (extension ring set —
                                                        spo/extension_rings.go names its lens
                                                        element differently from the port sets)
       `slug` and `lensDisplay` reproduce Go's slugify() and lensDisplay() exactly. */

    static slug(s) {
      return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
    }

    /** Go lensDisplay(): " + " becomes " and ", double spaces collapse. SPO's values carry the
     *  DISPLAY form, and url-sync matches on value or label, so the raw catalog name would not hit. */
    static lensDisplay(s) {
      return String(s || "").replace(/\s+\+\s+/g, " and ").replace(/\s{2,}/g, " ").trim();
    }

    /** SPO's toBase64Url, byte for byte — the escape/unescape pair is deliberate there for UTF-8. */
    static b64url(str) {
      return btoa(unescape(encodeURIComponent(str))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    }

    /** @param kind "flat" | "dome" | "ring" — each set names its lens element differently. Anything
     *  else (a neoprene cover, a spare sphere) has no brand/housing/lens question on its page, so it
     *  gets no parameter rather than one borrowed from another set. */
    spoParam(p, kind) {
      const h = this.byId.get(this.housing || "");
      if (!h || !this.lens) return "";
      const S = PortFinder.slug;
      const lensKey =
        kind === "flat" ? S(`fp-${S(p.lc)}-${S(p.m)}-lens-for-${h.n}-housing`)
        : kind === "dome" ? S(`dp-${S(p.er)}-lens-for-${h.n}-housing`)
        : kind === "ring" ? S(`specify-your-lens-for-${S(h.b)}-${S(h.n)}-housing`)
        : "";
      if (!lensKey) return "";
      const payload = {
        "camera-brand": h.b,
        [`housings-for-${S(h.b)}-cameras`]: h.n,
        [lensKey]: PortFinder.lensDisplay(this.lens),
      };
      try {
        return PortFinder.b64url(JSON.stringify(payload));
      } catch {
        return "";
      }
    }

    href(p, kind) {
      const q = new URLSearchParams();
      if (p.vid) q.set("variant", p.vid);
      const spo = this.spoParam(p, kind || p.k);
      if (spo) q.set("spo", spo);
      if (this.housing) q.set("housing", this.housing);
      const qs = q.toString();
      return `${this.rootUrl}/products/${p.h}${qs ? `?${qs}` : ""}`;
    }

    /** Whether a gear exists for this lens ON THIS HOUSING, stated under the lens selector.
     *
     *  Three states, and the empty one earns its place: for 37 of the 68 lenses that fit the A7RV/A7V
     *  there is no gear, nearly all of them primes, and a shopper who is told so stops hunting for one.
     *  Where there is one the link goes to its product page carrying the variant AND the housing, so the
     *  picker there opens already answered rather than asking the same two questions again. */
    paintGearNote(h, row) {
      const note = this.gearNote;
      if (!note) return;
      note.textContent = "";
      if (!h || !row) {
        note.hidden = true;
        return;
      }
      note.hidden = false;
      const g = row.g;
      const part = Array.isArray(g) && g[0] ? this.parts[g[0]] : null;
      if (!part) {
        note.className = "dvl-p__gearnote is-none";
        note.textContent = `No zoom or focus gear is listed for this lens on the ${h.n} — most primes need none.`;
        return;
      }
      const kind = part.type || "Zoom Gear";
      const paid = PAID_GEAR_HOUSINGS.has(h.id);
      note.className = `dvl-p__gearnote${paid ? " is-paid" : " is-free"}`;
      note.append(
        document.createTextNode(
          paid
            ? `A ${kind.toLowerCase()} fits this lens — `
            : `The ${kind.toLowerCase()} for this lens is included with a port purchase — `,
        ),
      );
      if (part.h) {
        const a = el("a", null, part.n);
        // the variant and the housing, so its picker opens on the right one instead of asking again
        const q = new URLSearchParams();
        if (g[1]) q.set("variant", String(g[1]));
        q.set("housing", h.id);
        a.href = `${this.rootUrl}/products/${part.h}?${q}`;
        note.append(a);
      } else {
        note.append(el("strong", null, part.n));
      }
      note.append(document.createTextNode(paid ? ", sold separately." : ", at no extra charge."));
    }

    /** One part, in the same card the gear finder uses — same class, same slots — so the two pages read
     *  as one product. `lead` marks the single resolved flat port as THE answer.
     *
     *  `opts.included` is for a part the port already contains: it shows what the shopper is getting and
     *  says so, with no price and no Add button, because the port's own line already carries it. Pricing
     *  it would invite paying twice for one gear; hiding it would leave the shopper unaware the port
     *  comes with the gear their lens needs. */
    card(p, lead, ring, opts = {}) {
      const li = el("li", `dvl-g__card${lead ? " dvl-p__lead" : ""}`);
      li.dataset.handle = p.h || "";
      li.dataset.vid = p.vid || "";

      // the media slot is always present: it is a square ratio box like a collection card's, and an
      // absent image must leave the card the same shape rather than collapsing it
      const shot = this.imgOf(p);
      if (shot) {
        const img = el("img", "dvl-g__thumb");
        img.src = `${shot}${shot.includes("?") ? "&" : "?"}width=400`;
        img.alt = "";
        img.loading = "lazy";
        li.append(img);
      } else {
        li.append(el("div", "dvl-g__thumb"));
      }
      const body = el("div", "dvl-g__card-body");
      body.append(el("span", "dvl-g__kind",
        p.k === "flat" ? "Flat port" : p.k === "dome" ? "Dome port" : p.type || "Add-on"));
      const name = el("h3", "dvl-g__card-name");
      const link = el("a", null, p.n);
      link.href = this.href(p, p.k);
      name.append(link);
      body.append(name);

      const bits = (p.k === "addon"
        ? [p.family || p.sku]
        : [p.m, p.k === "flat" ? p.mm && `${p.mm}mm` : p.sz, p.mat, p.surf ? "Surf Edition" : null]
      ).filter(Boolean).join(" · ");
      body.append(el("span", "dvl-g__sku", bits));

      // Included WITH A PORT PURCHASE, and still buyable: a gear is a small part that wears and gets
      // lost between dives, so a shopper may want a spare or a replacement. The badge says where the
      // free one comes from; the button below is for the extra.
      if (opts.included) body.append(el("span", "dvl-g__incl", "Included with a port purchase"));

      const foot = el("div", "dvl-g__card-foot");
      const price = el("span", "dvl-g__price dvl-g__price--sm", "—");
      // the visible words are gone, so this label is all a screen reader or a hover has left: it names
      // the port and, when there is one, the gear that comes with it free
      const gear = this.gearProperty(p);
      const add = addButton(
        opts.included
          ? `Add a spare ${p.n} to cart` // the port already brings one; this is an extra
          : gear
            ? `Add ${p.n} to cart, with the ${Object.values(gear)[0]} included free`
            : `Add ${p.n} to cart`,
      );
      add.disabled = true;
      add.addEventListener("click", () => this.add(p, ring, add));
      foot.append(price, add);
      body.append(foot);
      li.append(body);
      li._price = price;
      li._add = add;
      return li;
    }

    live(handle) {
      if (!this.cache.has(handle)) {
        this.cache.set(
          handle,
          fetch(`${this.rootUrl}/products/${handle}.js`)
            .then((r) => (r.ok ? r.json() : null))
            .catch(() => null),
        );
      }
      return this.cache.get(handle);
    }

    /** Live price + stock for every part now on screen. A handful of products, so no lazy observer. */
    async hydrate(scope) {
      for (const node of scope.querySelectorAll("[data-handle]")) {
        // a card shown as INCLUDED has no price and no Add button, and this loop awaits: touching them
        // threw on the first one and left every card after it stuck on the "—" placeholder. The included
        // gear leads the add-ons, so that was all of them.
        if (!node._price || !node._add) continue;
        const p = await this.live(node.dataset.handle);
        const v = p && p.variants.find((x) => String(x.id) === String(node.dataset.vid));
        if (!v) {
          node._price.textContent = "";
          continue;
        }
        if (v.available) setMoney(node._price, v.price, this.fmt);
        else node._price.textContent = "Sold out";
        node._add.disabled = !v.available;
        if (!v.available) {
          node._price.classList.add("dvl-g__oos");
          node._add.setAttribute("aria-label", "Sold out");
          node._add.title = "Sold out";
        }
      }
    }

    /** The gear that comes FREE with a port for the chosen lens, as a line item property.
     *
     *  Not a second line item: it is included in the port's price, so a line of its own would either
     *  charge for it or show a 0.00 the shopper has to interpret. SPO already records it this way on
     *  flat and dome ports - `displayOnCart` with the cart label below - so a cart assembled here and
     *  one assembled through SPO read identically. The label is SPO's exact wording, including for a
     *  focus gear, because matching it matters more than being pedantic about the type. */
    gearProperty(part) {
      // A PORT includes the gear. Grease, an O-ring, a cover or a spare sphere do not, and claiming
      // otherwise on their line would promise something the order does not contain.
      if (!part || (part.k !== "flat" && part.k !== "dome")) return null;
      const h = this.byId.get(this.housing || "");
      if (!h || PAID_GEAR_HOUSINGS.has(h.id)) return null; // sold here, so it is not a free inclusion
      const row = this.lens ? h.lenses.find((l) => l.l === this.lens) : null;
      const g = row && row.g;
      const gear = Array.isArray(g) && g[0] ? this.parts[g[0]] : null;
      return gear ? { "x1 Complimentary Zoom Gear Included": gear.n } : null;
    }

    async add(p, ring, btn) {
      btn.disabled = true;
      btn.classList.add("is-busy");
      // ONE line: a dome port product already contains its extension ring, so adding the ring as well
      // charged for a spare. The complimentary gear rides along as a property, not a line.
      const props = this.gearProperty(p);
      const items = [{ id: Number(p.vid), quantity: 1, ...(props ? { properties: props } : {}) }];
      try {
        const r = await fetch(`${this.rootUrl}/cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items }),
        });
        if (!r.ok) throw new Error(await r.text());
        this.say(
          `Added ${p.n}${props ? `, with the ${Object.values(props)[0]} included free` : ""}. ` +
            `<a href="${this.rootUrl}/cart">View cart</a>`,
        );
        btn.classList.remove("is-busy");
        markAdded(btn, `${p.n} added to cart`);
      } catch {
        this.say("That could not be added to the cart. Please try the product page.");
        btn.classList.remove("is-busy");
        btn.disabled = false;
      }
    }

    say(html) {
      this.toast.innerHTML = html;
      this.toast.hidden = false;
    }
  }

  const boot = () => document.querySelectorAll("[data-dvl-port-finder]").forEach((n) => new PortFinder(n));
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
